import { useEffect, useState } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction } from 'i18next';
import CustomModal from '../../../components/atoms/CustomModal';
import FormField from '../../../components/FormField/FormField';
import Button from '../../../components/atoms/Button';
import { useTranslation } from 'react-i18next';
import { MarketplacePlanItem } from '../../../types/marketplacePlans';

import PlanBenefitsSection from './PlanBenefitsSection';
import BenefitModal, { BenefitFormValues } from './BenefitModal';

export const editFreePlanSchema = (t: TFunction, silverLimit?: number) =>
  z.object({
    planName: z.string().optional(),
    productLimit: z.coerce
      .number({ invalid_type_error: t('acceptNumbersOnly') })
      .int(t('acceptNumbersOnly'))
      .min(1, t('acceptNumbersOnly'))
      .refine(
        (val) => silverLimit === undefined || val < silverLimit,
        {
          message: t('home.free-plan-limit-must-be-less-than-silver', {
            limit: silverLimit,
          }),
        }
      ),
    benefits: z
      .array(z.object({ textEn: z.string(), textAr: z.string() }))
      .max(5),
  });

export type EditPlanFormValues = z.infer<ReturnType<typeof editFreePlanSchema>>;

interface EditPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: MarketplacePlanItem | null;
  silverLimit?: number;
  isSaving?: boolean;
  onSave?: (updatedPlan: {
    id: string;
    limit: number;
    informationsEn: string[];
    informationsAr: string[];
  }) => void;
}

export default function EditPlanModal({
  isOpen,
  onClose,
  plan,
  silverLimit,
  isSaving = false,
  onSave,
}: EditPlanModalProps) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const [benefitModalOpen, setBenefitModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const methods = useForm<EditPlanFormValues>({
    resolver: zodResolver(editFreePlanSchema(t, silverLimit)),
    mode: 'onChange',
    defaultValues: {
      planName: '',
      productLimit: 1,
      benefits: [],
    },
  });

  const { isDirty } = methods.formState;
  const { fields, append, remove, update } = useFieldArray({
    control: methods.control,
    name: 'benefits',
  });

  useEffect(() => {
    if (plan) {
      const localizedPlanName = isArabic
        ? plan.planNameAr || plan.planNameEn
        : plan.planNameEn || plan.planNameAr;

      methods.reset({
        planName: localizedPlanName || '',
        productLimit: plan.productLimit ?? 1,
        benefits: (plan.informationsEn || []).map((textEn, idx) => ({
          textEn,
          textAr: plan.informationsAr?.[idx] || '',
        })),
      });
    } else {
      methods.reset({
        planName: '',
        productLimit: 1,
        benefits: [],
      });
    }
  }, [plan, isOpen, methods, isArabic]);

  const handleOpenAddBenefit = () => {
    if (fields.length >= 5) return;
    setEditingIndex(null);
    setBenefitModalOpen(true);
  };

  const handleOpenEditBenefit = (_benefit: { textEn: string; textAr: string }, index: number) => {
    setEditingIndex(index);
    setBenefitModalOpen(true);
  };

  const handleDeleteBenefit = (index: number) => {
    remove(index);
  };

  const handleSaveBenefit = (values: BenefitFormValues) => {
    if (editingIndex !== null) {
      update(editingIndex, { textEn: values.benefitTextEn, textAr: values.benefitTextAr });
    } else {
      append({ textEn: values.benefitTextEn, textAr: values.benefitTextAr });
    }
    setBenefitModalOpen(false);
  };

  const onSubmit = (data: EditPlanFormValues) => {
    if (plan && onSave) {
      onSave({
        id: plan.id,
        limit: Number(data.productLimit) || 0,
        informationsEn: data.benefits.map((b) => b.textEn),
        informationsAr: data.benefits.map((b) => b.textAr),
      });
    }
  };

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  // Map useFieldArray fields to the shape PlanBenefitsSection expects
  const benefitItems = fields.map((f, idx) => ({
    id: f.id,
    textEn: f.textEn,
    textAr: f.textAr,
    _idx: idx,
  }));

  // Derive editingBenefit for BenefitModal from fields + editingIndex
  const editingBenefitForModal =
    editingIndex !== null && fields[editingIndex]
      ? { id: fields[editingIndex].id, textEn: fields[editingIndex].textEn, textAr: fields[editingIndex].textAr }
      : null;

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('home.edit-plan-modal-title')}
        subTitle={t('home.edit-plan-modal-desc')}
        width={600}
        closeIconClassName="[&_.ant-modal-close]:!top-6"
      >
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-col gap-6 mt-4"
            data-testid="edit-plan-modal-form-container"
          >
            {/* Field 1: Plan Name */}
            <div
              className="w-full"
              data-testid="edit-plan-name-field-container"
            >
              <FormField
                type="text"
                id="plan-name-input"
                name="planName"
                label={t('home.plan-name-label')}
                placeholder={t('home.select-product-placeholder')}
                className="!rounded-xl"
                disabled
                data-testid="edit-plan-name-input"
              />
            </div>

            {/* Field 2: Product Limit */}
            <div
              className="w-full"
              data-testid="edit-plan-limit-field-container"
            >
              <FormField
                type="number"
                id="product-limit-input"
                name="productLimit"
                label={t('home.product-limit-label')}
                placeholder={t('home.set-limit-placeholder')}
                className="!rounded-xl"
                required
                min={1}
                data-testid="edit-plan-limit-input"
              />
            </div>

            {/* Plan Benefits Section */}
            <PlanBenefitsSection
              benefits={benefitItems}
              isRecommended={false}
              onOpenAddBenefit={handleOpenAddBenefit}
              onOpenEditBenefit={handleOpenEditBenefit}
              onDeleteBenefit={handleDeleteBenefit}
              onToggleRecommended={() => { }}
              hideRecommended
            />

            {/* Modal Actions */}
            <div
              className="flex justify-end items-center gap-3 pt-4"
              data-testid="edit-plan-modal-actions"
            >
              <Button
                variant="outline"
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className="rounded-xl px-6 py-2"
                data-testid="edit-plan-cancel-btn"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="dark"
                type="submit"
                loading={isSaving}
                disabled={!isDirty || isSaving}
                className="rounded-xl px-6 py-2"
                data-testid="edit-plan-save-btn"
              >
                {t('home.save-plan')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </CustomModal>

      {/* Benefit Add/Edit Modal */}
      <BenefitModal
        isOpen={benefitModalOpen}
        editingBenefit={editingBenefitForModal}
        onClose={() => setBenefitModalOpen(false)}
        onSave={handleSaveBenefit}
      />
    </>
  );
}
